/****************************************************
 * Module Name:  TenderStoneDetailPreviewModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, Tooltip, Stack, Typography } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Constants from "../../../utility/constants";
import Utility from "../../../utility/utility";
import renderCellExpand from "../../common/dataGridCellExpand";
import { LoadingButton } from "@mui/lab";
import { IoIosLock } from "react-icons/io";
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import CommonSnackbar from "../../common/commonSnackBar";
import logo from '../../../images/pansuriya.jpg'
import { Modal } from "react-bootstrap";

const TenderStoneDetailPreviewModal = ({ tenderStoneId, handleSave, isStoneWise, show, handleClose, }) => {
    const [listData, setListData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [outerSelectionModel, setOuterSelectionModel] = useState([]);
    const [outerLoader, setOuterLoader] = useState();
    const [avgTotal, setAvgTotal] = useState(0);
    const [columnNames, setColumnNames] = React.useState([]);

    useEffect(() => {
        handleSearch(tenderStoneId);
    }, []);

    const handleSearch = (tenderStoneId) => {
        setOuterLoader(true);
        let bidstatus = isStoneWise ? "COMPLETED" : "LOCKED";
        TenderLotBidProxy.GetTenderLotPricePlanDataByTenderStoneidAndStatusPreview(tenderStoneId, bidstatus, "LOCKED", (response) => {
            if (response) {
                const transformedData = response.map(item => ({
                    ...item,
                    isHighlighted: false,
                    note: "",
                    priceplanningdetail: item.priceplanningdetail.map(detail => ({
                        ...detail,
                        stonetype: detail.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                    })),
                }));

                setListData(transformedData);
                prepareColumns({});
                setOuterLoader(false);
            } else {
                setOuterLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        })
    }

    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "totalsize":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_SELL_BID_DISC,
                        Constants.COLUMN_PERMISSION.MFG_MFG_BID_DISC,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const prepareInnerColumns = (uniqueFilterValues) => {
        const columnNames = innercolumnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "discount":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_SELL_BID_DISC,
                        Constants.COLUMN_PERMISSION.MFG_MFG_BID_DISC
                    ];
                    break;
                case "finalprice":
                    guids = [
                        Constants.COLUMN_PERMISSION.MFG_SELL_BID_LOCKED_BY,
                        Constants.COLUMN_PERMISSION.MFG_MFG_BID_LOCKED_BY,
                        Constants.COLUMN_PERMISSION.MFG_SELL_BID_LOCKED_AT,
                        Constants.COLUMN_PERMISSION.MFG_MFG_BID_LOCKED_AT,
                    ];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, innercolumnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        return columnNames;
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const handleSubmit = () => {
        let tenderstoneid;
        let tenderlotid;
        let priceplanid = [];
        let planid;
        let totalprice = avgTotal;

        setLoader(true);
        listData.forEach(iData => {
            if (iData.embededkey === outerSelectionModel.at(0)) {
                tenderstoneid = iData.priceplanningdetail[0].tenderstoneid;
                tenderlotid = iData.priceplanningdetail[0].tenderlotid;
                planid = iData.planid;
                iData.priceplanningdetail.forEach(iRow => priceplanid.push(iRow.id));

            }
        })

        if (handleSave) {
            handleSave(tenderstoneid, tenderlotid, priceplanid, planid, totalprice);
        }
    }

    useEffect(() => {
        let avgTotalValue = 0;

        if (outerSelectionModel.length > 0) {
            let firstRowToHighlight = outerSelectionModel[0];
            const transformedData = listData.map(item => {
                const isHighlighted = outerSelectionModel.includes(item.embededkey);

                if (isHighlighted) {
                    avgTotalValue += item.finalprice_mf; // Accumulate total price for selected items
                }
                if (firstRowToHighlight === item.embededkey) {
                    return {
                        ...item,
                        isHighlighted: isHighlighted,
                        note: "This plan will be locked with above average total",
                    };
                }
                else {
                    return {
                        ...item,
                        isHighlighted: false,
                        note: "",
                    };
                }
            });

            setAvgTotal((avgTotalValue / outerSelectionModel.length).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
            setListData(transformedData);

        } else {
            const transformedData = listData.map(item => {
                setAvgTotal(0);
                return {
                    ...item,
                    isHighlighted: false,
                    note: "",
                };

            });
            setListData(transformedData);
        }
    }, [outerSelectionModel]);

    const columnsWithPermission = {
        "92220e56-ce36-48ea-b8c7-5ef94430eb42": {
            field: 'finalprice',
            headerName: 'SALES BID TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        "8f5e9fc3-a4b7-49e9-ade5-7915b5f95c31": {
            field: 'finalprice_mf',
            headerName: 'MANUFACTURING BID TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 200
        },
    };

    const columnsWithoutPermission = [
        {
            field: 'planid',
            headerName: 'PLAN ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalcount',
            headerName: 'TOTAL COUNT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'totalsize',
            headerName: 'TOTAL SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 180
        },
        {
            field: 'note',
            headerName: 'NOTE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 350
        },
    ];

    const innercolumnsWithPermission = {
        "92220e56-ce36-48ea-b8c7-5ef94430eb42": {
            field: 'sellbiddisc',
            headerName: 'SELL BID DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 100,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (params.value).toFixed(2),
            cellClassName: (params) => 'lavenderCell'
        },
        "8f5e9fc3-a4b7-49e9-ade5-7915b5f95c31": {
            field: 'mfgbiddisc',
            headerName: 'MFG BID DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 100,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (params.value).toFixed(2),
            cellClassName: (params) => 'lavenderCell'
        },
        "7f0b2670-e6aa-4bbf-a7e8-b1e302c1faf3": {
            field: 'sellbidlockedbyusername',
            headerName: 'SELL BID LOCKED BY USERNAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 230
        },
        "6a67cee0-f2fb-442f-b257-911213e2e71b": {
            field: 'mfgbidlockedbyusername',
            headerName: 'MFG BID LOCKED BY USERNAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 230
        },
        "a54d9c22-779d-4323-a931-5eb8af217277": {
            field: 'sellbidlockedat',
            headerName: 'SELL BID LOCKED AT',
            headerAlign: 'center',
            align: 'center',
            width: 150,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        "bde5dd1e-3088-4c58-90bd-aa4a0ae18cdd": {
            field: 'mfgbidlockedat',
            headerName: 'MFG BID LOCKED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
    };

    const innercolumnsWithoutPermission = [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 121
        },
        {
            field: 'size',
            headerName: 'CTS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 76
        },
        {
            field: 'colortype',
            headerName: 'COL TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'color',
            headerName: 'COL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 30
        },
        {
            field: 'fancycolor1',
            headerName: 'FANCY COLOR 1',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fancycolor2',
            headerName: 'FANCY COLOR 2',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fromintensity',
            headerName: 'FROM INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 125
        },
        {
            field: 'tointensity',
            headerName: 'TO INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'overtone',
            headerName: 'OVERTONE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 90
        },
        {
            field: 'clarity',
            headerName: 'CLR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60,
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 130
        },
        {
            field: 'iscertified',
            headerName: 'CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 80
        },
        {
            field: 'noofstones',
            headerName: 'STONES',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 75,
            renderHeader: () => (
                <Tooltip title="NO. OF STONES" placement="bottom">
                    <span>STONES</span>
                </Tooltip>
            ),
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (params.value).toFixed(2),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 60
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'labprice',
            headerName: 'LAB $',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'stoneprice',
            headerName: 'STONE $',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 90
        },
        {
            field: 'total',
            headerName: 'TOTAL $',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 90,
        },
        {
            field: 'finalprice',
            headerName: 'FINAL $',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 85,
            renderHeader: () => (
                <Tooltip title="TOTAL $ * NO. OF STONES" placement="bottom">
                    <span>FINAL $</span>
                </Tooltip>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: 'tenderlotbidstatus',
            headerName: 'TENDER LOT BID STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 170
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
    ];

    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getDetailPanelContent = React.useCallback((row, rowsx) => <DetailPanelContent row={row} rowsx={rowsx} />,
        [],);

    function DetailPanelContent({ row: rowProp, }) {

        const middlerows = rowProp.priceplanningdetail;

        const uniqueFilterValues = {
            shape: Utility.getDistinctOptions(middlerows, "shape"),
            color: Utility.getDistinctOptions(middlerows, "color"),
            cut: Utility.getDistinctOptions(middlerows, "cut"),
            pol: Utility.getDistinctOptions(middlerows, "pol"),
            sym: Utility.getDistinctOptions(middlerows, "sym"),
            clarity: Utility.getDistinctOptions(middlerows, "clarity"),
            flo: Utility.getDistinctOptions(middlerows, "flo"),
            stonetype: Utility.getDistinctOptions(middlerows, "stonetype"),
            sellbidlockedbyusername: Utility.getDistinctOptions(middlerows, "sellbidlockedbyusername"),
            mfgbidlockedbyusername: Utility.getDistinctOptions(middlerows, "mfgbidlockedbyusername"),
            lab: Utility.getDistinctOptions(middlerows, "lab"),
            tenderlotbidstatus: Utility.getDistinctOptions(middlerows, "tenderlotbidstatus"),
            status: Utility.getDistinctOptions(middlerows, "status"),
            colortype: Utility.getDistinctOptions(middlerows, "colortype"),
            fancycolor1: Utility.getDistinctOptions(middlerows, "fancycolor1"),
            fancycolor2: Utility.getDistinctOptions(middlerows, "fancycolor2"),
            fromintensity: Utility.getDistinctOptions(middlerows, "fromintensity"),
            tointensity: Utility.getDistinctOptions(middlerows, "tointensity"),
            overtone: Utility.getDistinctOptions(middlerows, "overtone"),
        };

        let middlecolumns = prepareInnerColumns(uniqueFilterValues);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "83vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={middlecolumns}
                        rows={middlerows}
                        getRowId={(row) => row.id}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                                minHeight: "30px"
                            },
                            "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                                margin: 0
                            },
                            maxHeight: "45vh"
                        }}
                        // loading={isLoading}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false,
                                    tenderlotbidstatus: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <Modal
            enforceFocus={false}
            show={show}
            onHide={() => handleClose()}
            dialogClassName="max-modalSize"
            style={{ zIndex: 1050 }}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <img
                        src={logo}
                        height="40pt"
                        width="40pt"
                        alt={Constants.COMPANY_SITE}
                    />
                    &nbsp;&nbsp;
                    Tender Stone Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    {showSnackbar ? (
                        <CommonSnackbar
                            show={true}
                            message={snackbarMessage}
                            variant={snackbarVariant}
                            autoHide={Constants.SnackBarCloseTimer}
                            title={snackbarVariant === "success" ? "Success" :
                                snackbarVariant === "error" ? "Failed" : "Warning"}
                            handleClose={() => setShowSnackbar(false)}
                        />
                    ) : null}
                    <div>
                        <Box
                            sx={{
                                px: 1,
                                width: "100%",
                                position: "relative",
                                backgroundColor: '#fff',
                                color: '#000',
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "4px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                                    color: "#1976d2",
                                    padding: "5px 5px",
                                    borderRadius: "4px",
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "75vh",
                            }}
                        >
                            <DataGridPro
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                        fontWeight: Constants.defaultFontWeight,
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "6px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    "& .lavenderCell": {
                                        color: "#080808",
                                        backgroundColor: "#D8BFD8",
                                    },
                                    "& .highlighted-row": {
                                        color: "#080808",
                                        backgroundColor: "#B7ECEA !important",
                                    },
                                    "& .unhighlighted-row": {
                                        backgroundColor: "#FFFFFF !important",
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                density="compact"
                                disableRowSelectionOnClick={true}
                                slots={{ toolbar: GridToolbar }}
                                rows={listData}
                                columns={columnNames}
                                getRowId={(row) => row.embededkey}
                                loading={outerLoader}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns id, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                getRowClassName={(params) => {
                                    return params.row.isHighlighted
                                        ? "highlighted-row"
                                        : "unhighlighted-row"
                                }
                                }
                                onRowSelectionModelChange={(newSelectionModel) => {
                                    setOuterSelectionModel(newSelectionModel);
                                }}
                                rowSelectionModel={outerSelectionModel}
                                checkboxSelection={isStoneWise}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: {
                                            debounceMs: 500,
                                        },
                                    },
                                }}
                                getDetailPanelHeight={getDetailPanelHeight}
                                getDetailPanelContent={({ row }) => getDetailPanelContent(row, listData)}
                            />
                            {isStoneWise ? (<Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    position: "absolute",
                                    top: 5,
                                    left: "480px",
                                }}
                            >
                                <Box sx={{ padding: '7px', border: '1px solid #ccc', borderRadius: '6px', display: 'inline-block', height: '32px' }}>
                                    <Typography sx={{ fontSize: 12, color: '#333', fontWeight: 'bold' }}>
                                        AVG TOTAL : {avgTotal}
                                    </Typography>
                                </Box>
                            </Box>) : null}

                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    position: "absolute",
                                    top: 3,
                                    right: "270px",
                                }}
                            >

                                {outerLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => handleSearch(tenderStoneId)}
                                            color="primary"
                                            loading={outerLoader}
                                            fontSize="medium"
                                            sx={{ cursor: "pointer", mr: 1 }}
                                        />
                                    </Tooltip>
                                )}

                                {isStoneWise ? (<Box sx={{
                                    "& .Mui-disabled": {
                                        backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                        color: "rgba(0, 0, 0, 0.26) !important"
                                    },
                                }}>
                                    <LoadingButton
                                        sx={{ mr: 1 }}
                                        variant="contained"
                                        color="info"
                                        onClick={(e) => handleSubmit()}
                                        loading={loader}
                                        loadingPosition="start"
                                        startIcon={<IoIosLock />}
                                        type="Submit"
                                        disabled={outerSelectionModel.length === 0}
                                    >
                                        LOCK
                                    </LoadingButton>
                                </Box>) : null}
                            </Box>
                        </Box>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default TenderStoneDetailPreviewModal;